'use client';

import './giving-history.scss';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { Select } from '@sola/ark-core/molecules/select/select.tsx';
import {
  useReadyForQueries,
  useSupporterQuery,
} from '@sola/supporter-sdk/hooks/base.ts';
import { useGivingHistory } from '@sola/supporter-sdk/hooks/giving-history.ts';
import { commitmentsQuery, pledgesQuery } from '@sola/supporter-sdk/queries.ts';

import { PrimerError } from '../../molecules/primer-error/primer-error.tsx';
import { PaymentIssueAlert } from '../payment-issue-alert/payment-issue-alert.tsx';
import { GivingHistoryMonth } from './giving-history-month.tsx';
import { getHistoryItemsByMonth, getYearList } from './giving-history.logic.ts';

/**
 * Get component data
 */
export const useComponentData = () => {
  const {
    data,
    isFetching: commitmentsFetching,
    isFetched: commitmentsFetched,
  } = useSupporterQuery([commitmentsQuery, pledgesQuery]);

  const { t } = useTranslation('my');
  const { isReady } = useReadyForQueries();
  const [commitments = [], pledges = []] = data;

  const [year, setYear] = useState(new Date().getFullYear());
  const {
    data: givingHistoryItems,
    isLoading,
    isFetching,
    isPending,
    isError,
  } = useGivingHistory({
    start: `${year}-01-01`,
    end: `${year}-12-31`,
  });
  /** NOTE: Could be configured in CMS */
  const numYearsToDisplay = 7;

  /**
   * Handle year change.
   */
  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setYear(Number(e.target.value));
  };

  /**
   * Group and format items for rendering.
   */
  const groupedItems = useMemo(() => {
    if (!givingHistoryItems?.length) {
      return null;
    }
    const grouped =
      givingHistoryItems.length > 0
        ? getHistoryItemsByMonth(
            year,
            givingHistoryItems,
            commitments,
            pledges,
            t,
          )
        : [];

    return grouped;
    // We want to run logic either when item date change or when commitments initially load.
  }, [givingHistoryItems?.[0]?.recordDate, commitmentsFetched]);

  const hasQueryResults =
    !(isLoading || isFetching || isPending || commitmentsFetching) || isError;

  const isShowingSkeletons = !isReady || isFetching || !hasQueryResults;
  return {
    isShowingSkeletons,
    yearList: getYearList(new Date(), numYearsToDisplay),
    hasData: !!givingHistoryItems?.length && groupedItems,
    hasError: isError,
    groupedItems,
    handleYearChange,
  };
};

/**
 * Displays list of commitments based on frequency.
 */
export const GivingHistory = () => {
  const { t } = useTranslation('my');

  const {
    isShowingSkeletons,
    hasData,
    hasError,
    groupedItems,
    yearList,
    handleYearChange,
  } = useComponentData();

  return (
    <div className="max-w-md flex flex-col gap-comfy-xl">
      <PrimerError keysToAlert={['commitments']} />

      {!isShowingSkeletons && <PaymentIssueAlert onlyShowMissingPayment />}

      <div className="flex flex-col gap-snug-lg">
        <div className="GivingHistory__yearselect">
          <Select
            label={t('givingHistory.title')}
            items={yearList.map((y) => {
              return {
                value: y.toString(),
              };
            })}
            onChange={handleYearChange}
            disabled={isShowingSkeletons}
          />
        </div>
      </div>

      {isShowingSkeletons && <GivingHistoryMonth isLoading />}

      {!isShowingSkeletons &&
        hasData &&
        groupedItems!.map((givingMonthGroup) => (
          <GivingHistoryMonth
            key={givingMonthGroup.heading}
            {...givingMonthGroup}
          />
        ))}

      {!isShowingSkeletons && !hasData && !hasError && (
        <Alert message={t('givingHistory.noHistory')} />
      )}

      {!isShowingSkeletons && hasError && (
        <Alert
          title={t('givingHistory.requestError.title')}
          message={t('givingHistory.requestError.message')}
          variant="negative"
          action={
            <Link href={t('ctas.contactSupport.url')}>
              {t('ctas.contactSupport.text')}
            </Link>
          }
        />
      )}
    </div>
  );
};
